"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePathname, useRouter } from "next/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import {
  IconExternalLink,
  IconLoader2,
  IconPencil,
  IconPhone,
  IconTrash,
} from "@tabler/icons-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { usePermissions } from "@/hooks/use-permissions";
import { useAuthStore } from "@/stores/auth-store";
import { getAppointment, updateAppointmentStatus } from "@/lib/appointments-api";
import { checkInAppointment, type CheckinStep } from "@/lib/appointment-workflow-api";
import { STATUS_BADGE_VARIANT, useAppointmentStatusLabel } from "@/lib/appointment-status";
import {
  AppointmentEditor,
  type AppointmentDraft,
} from "@/components/appointments/appointment-editor";
import { DeleteAppointmentDialog } from "@/components/appointments/delete-appointment-dialog";
import { SendReminderDialog } from "@/components/appointments/send-reminder-dialog";
import { AppointmentSheetActions } from "@/components/appointments/sheet/appointment-sheet-actions";
import { AppointmentSheetView } from "@/components/appointments/sheet/appointment-sheet-view";
import type { SheetClosedInfo } from "@/components/appointments/sheet/appointment-sheet-context";
import type { Appointment, AppointmentStatus, Lead } from "@/types";

// Apollo deposit-modal design language — shared with the editor.
const LABEL_CLS =
  "text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground";

export interface AppointmentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Present ⇒ view/edit an existing row; null ⇒ create from `draft`. */
  appointmentId: string | null;
  draft: AppointmentDraft | null;
  initialEdit: boolean;
  onChanged?: () => void;
  onClosed?: (info: SheetClosedInfo) => void;
}

type Mode = "view" | "edit";

/**
 * Appointments rebuild 86-01 (D4): the appointment sheet. Right-side on
 * desktop, full-screen on the phone; opens in VIEW (header + readout +
 * actions) and turns its fields editable in place on Edit. Replaces the show
 * modal, the form dialog and the mobile wizard's last steps.
 */
export function AppointmentSheet({
  open,
  onOpenChange,
  appointmentId,
  draft,
  initialEdit,
  onChanged,
  onClosed,
}: AppointmentSheetProps) {
  const t = useTranslations("appointments.sheet");
  const tq = useTranslations("appointmentQuickActions");
  const statusLabel = useAppointmentStatusLabel();
  const router = useRouter();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canEdit = can("appointments.edit");
  const canDelete = can("appointments.delete");
  const remindersEnabled = useAuthStore(
    (s) => s.user?.feature_flags?.appointment_reminders_enabled ?? false
  );

  // Create mode flips to view mode on the row it just made.
  const [createdId, setCreatedId] = useState<string | null>(null);
  const activeId = appointmentId ?? createdId;
  const [mode, setMode] = useState<Mode>(appointmentId && !initialEdit ? "view" : "edit");
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [reminderOpen, setReminderOpen] = useState(false);
  const [remindersVersion, setRemindersVersion] = useState(0);
  const [actionPending, setActionPending] = useState(false);
  /** Create mode: the patient the editor has picked, mirrored in the header. */
  const [draftLead, setDraftLead] = useState<Lead | null>(null);

  // What to do once the user confirms discarding: close, or drop back to view.
  const afterDiscardRef = useRef<"close" | "view">("close");
  // Whether anything was written while open — reported to the opener on close.
  const changedRef = useRef(false);
  const closedRef = useRef(false);

  // The ONE way out: every close path reports once to the opener.
  const close = useCallback(() => {
    onOpenChange(false);
    if (closedRef.current) return;
    closedRef.current = true;
    onClosed?.({ changed: changedRef.current });
  }, [onOpenChange, onClosed]);

  // The record lives in the react-query cache: a re-open of a recently viewed
  // row paints instantly from cache while the fresh GET lands, and every
  // optimistic change below writes the cache (revert = write the previous).
  const queryKey = ["appointments", "detail", activeId] as const;
  const query = useQuery({
    queryKey,
    queryFn: () => getAppointment(activeId as string),
    enabled: !!activeId,
  });
  const appt = query.data ?? null;
  const loading = query.isLoading;
  const loadError = query.isError;
  const setAppt = useCallback(
    (next: Appointment | ((current: Appointment | null) => Appointment)) => {
      queryClient.setQueryData<Appointment>(queryKey, (current) =>
        typeof next === "function" ? next(current ?? null) : next
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryClient, activeId]
  );

  // Every mutation funnels here: the calendar feed (react-query) and the
  // surface that opened the sheet both refresh.
  const notifyChanged = useCallback(() => {
    changedRef.current = true;
    void queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
    void queryClient.invalidateQueries({ queryKey: ["appointments", "availability"] });
    // The phone keys everything under "m" (today, agenda, book).
    void queryClient.invalidateQueries({ queryKey: ["m"] });
    onChanged?.();
  }, [queryClient, onChanged]);

  // Hard navigation guard (reload / tab close) while a form is dirty. The App
  // Router exposes no route-change event; Escape / overlay / Cancel all go
  // through requestClose below.
  useEffect(() => {
    if (!dirty || mode !== "edit") return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty, mode]);

  const requestClose = (next: boolean) => {
    if (next) {
      onOpenChange(true);
      return;
    }
    if (mode === "edit" && dirty) {
      afterDiscardRef.current = "close";
      setConfirmDiscard(true);
      return;
    }
    close();
  };

  const cancelEdit = () => {
    const fallback: "close" | "view" = appt ? "view" : "close";
    if (dirty) {
      afterDiscardRef.current = fallback;
      setConfirmDiscard(true);
      return;
    }
    if (fallback === "view") {
      setMode("view");
    } else {
      close();
    }
  };

  const confirmDiscardNow = () => {
    setConfirmDiscard(false);
    setDirty(false);
    if (afterDiscardRef.current === "view" && appt) {
      setMode("view");
    } else {
      close();
    }
  };

  const handleSaved = (saved: Appointment) => {
    queryClient.setQueryData<Appointment>(["appointments", "detail", saved.id], saved);
    if (!appointmentId) setCreatedId(saved.id);
    setDirty(false);
    setMode("view");
    setRemindersVersion((v) => v + 1);
    notifyChanged();
  };

  // Optimistic status change with revert — the header pill and the actions
  // row move first, the PATCH follows, a failure snaps them back.
  const handleStatus = async (status: AppointmentStatus) => {
    if (!appt || actionPending) return;
    const previous = appt;
    setActionPending(true);
    setAppt({ ...appt, status });
    try {
      const updated = await updateAppointmentStatus(appt.id, status);
      setAppt((current) => (current ? { ...current, ...updated } : updated));
      toast.success(tq("toast.updated"));
      notifyChanged();
    } catch {
      setAppt(previous);
      toast.error(tq("toast.failed"));
    } finally {
      setActionPending(false);
    }
  };

  const handleCheckIn = async (step: CheckinStep) => {
    if (!appt || actionPending) return;
    const previous = appt;
    setActionPending(true);
    const nowIso = new Date().toISOString();
    // Cumulative stamps, never overwritten — mirror the backend locally.
    const optimistic: Appointment = { ...appt };
    if (step === "arrived") {
      optimistic.time_arrived = optimistic.time_arrived ?? nowIso;
    } else if (step === "seated") {
      optimistic.time_arrived = optimistic.time_arrived ?? nowIso;
      optimistic.time_seated = optimistic.time_seated ?? nowIso;
    } else if (step === "dismissed") {
      optimistic.time_arrived = optimistic.time_arrived ?? nowIso;
      optimistic.time_seated = optimistic.time_seated ?? nowIso;
      optimistic.time_dismissed = optimistic.time_dismissed ?? nowIso;
      optimistic.status = "completed";
    }
    setAppt(optimistic);
    try {
      const updated = await checkInAppointment(appt.id, step);
      setAppt((current) => (current ? { ...current, ...updated } : updated));
      toast.success(tq("toast.updated"));
      notifyChanged();
    } catch {
      setAppt(previous);
      toast.error(tq("toast.failed"));
    } finally {
      setActionPending(false);
    }
  };

  const handleDeleted = () => {
    notifyChanged();
    close();
  };

  // The phone tree has its own patient screen; the desk goes to the lead page.
  const openPatient = (leadId: string) => {
    close();
    router.push(pathname.startsWith("/m") ? `/m/patients/${leadId}` : `/leads/${leadId}`);
  };

  const isCreate = !appointmentId;
  const lead = appt?.lead ?? (draftLead ? { id: draftLead.id, full_name: draftLead.full_name, phone: draftLead.phone, email: draftLead.email } : null);
  const eyebrow = isCreate ? t("newEyebrow") : mode === "edit" ? t("editEyebrow") : t("eyebrow");
  const title = appt?.display_title ?? (isCreate ? t("newTitle") : t("untitled"));

  return (
    <Sheet open={open} onOpenChange={requestClose}>
      <SheetContent
        className="flex w-full flex-col gap-0 overflow-hidden p-0 sm:max-w-[540px]"
        data-slot="appointment-sheet"
        data-mode={mode}
        onInteractOutside={(e) => {
          // A toast click or the conflict panel's own dialogs must not tear
          // down a dirty form behind the user's back.
          if (mode === "edit" && dirty) e.preventDefault();
        }}
      >
        <SheetHeader className="shrink-0 gap-0 border-b px-6 pb-4 pt-5 text-left">
          <div className="flex items-center justify-between gap-3 pr-8">
            <span className={LABEL_CLS}>{eyebrow}</span>
            {appt && (
              <Badge variant={STATUS_BADGE_VARIANT[appt.status]} className="shrink-0">
                {statusLabel(appt.status)}
              </Badge>
            )}
          </div>
          <SheetTitle className="mt-1.5 text-2xl font-semibold leading-tight tracking-tight">
            {title}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {isCreate ? t("newTitle") : t("eyebrow")}
          </SheetDescription>

          {lead && (
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12.5px] text-muted-foreground">
              <button
                type="button"
                className="font-medium text-foreground hover:underline"
                onClick={() => openPatient(lead.id)}
              >
                {lead.full_name}
              </button>
              {lead.phone && (
                <a
                  href={`tel:${lead.phone}`}
                  className="inline-flex items-center gap-1 font-mono text-[12px] hover:text-foreground hover:underline"
                >
                  <IconPhone className="size-3.5" aria-hidden />
                  {lead.phone}
                </a>
              )}
            </div>
          )}

          {mode === "view" && appt && canEdit && (
            <AppointmentSheetActions
              appointment={appt}
              pending={actionPending}
              onStatus={(status) => void handleStatus(status)}
              onCheckIn={(step) => void handleCheckIn(step)}
            />
          )}
        </SheetHeader>

        {loading && !appt ? (
          <div className="flex flex-1 items-center justify-center py-16 text-muted-foreground">
            <IconLoader2 className="size-5 animate-spin" aria-label={t("loading")} />
          </div>
        ) : loadError && !appt ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center text-sm text-muted-foreground">
            <p>{t("loadFailed")}</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              {t("retry")}
            </Button>
          </div>
        ) : mode === "edit" ? (
          <AppointmentEditor
            appointment={appt ?? undefined}
            draft={draft ?? undefined}
            onSaved={handleSaved}
            onCancel={cancelEdit}
            onDirtyChange={setDirty}
            onLeadChange={setDraftLead}
          />
        ) : appt ? (
          <>
            <AppointmentSheetView
              appointment={appt}
              remindersEnabled={remindersEnabled}
              remindersVersion={remindersVersion}
              onSendReminder={() => setReminderOpen(true)}
            />

            {/* Footer band — destructive/secondary as icon tiles on the left,
                the two primary moves on the right. */}
            <div className="flex shrink-0 items-center gap-2 border-t bg-muted/40 px-6 py-3.5">
              {canDelete && (
                <Button
                  variant="outline"
                  size="icon"
                  className="text-destructive hover:text-destructive"
                  aria-label={t("delete")}
                  title={t("delete")}
                  onClick={() => setDeleteOpen(true)}
                >
                  <IconTrash className="size-4" />
                </Button>
              )}
              <span className="flex-1" />
              {canEdit && (
                <Button variant="ghost" onClick={() => setMode("edit")}>
                  <IconPencil className="mr-1.5 size-4" />
                  {t("edit")}
                </Button>
              )}
              {appt.lead?.id && (
                <Button onClick={() => openPatient(appt.lead!.id)}>
                  <IconExternalLink className="mr-1.5 size-4" />
                  {t("openPatient")}
                </Button>
              )}
            </div>
          </>
        ) : null}
      </SheetContent>

      {appt && (
        <>
          <DeleteAppointmentDialog
            open={deleteOpen}
            onOpenChange={setDeleteOpen}
            appointment={appt}
            onDeleted={handleDeleted}
          />
          <SendReminderDialog
            open={reminderOpen}
            onOpenChange={setReminderOpen}
            appointment={appt}
            onSuccess={() => setRemindersVersion((v) => v + 1)}
          />
        </>
      )}

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("unsaved.title")}</AlertDialogTitle>
            <AlertDialogDescription>{t("unsaved.body")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("unsaved.keep")}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscardNow}>{t("unsaved.discard")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Sheet>
  );
}
